import React from 'react';
import SEO from '../components/SEO';

const Terms: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms and Conditions',
    description: 'Terms and Conditions for JackpotQuantum.com - Read our terms of use and service agreement.',
  };

  return (
    <>
      <SEO
        title="Terms and Conditions - JackpotQuantum.com"
        description="Read our Terms and Conditions to understand the rules and regulations for using JackpotQuantum.com and our services."
        canonical="/terms"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">Terms &</span> <span className="text-white">Conditions</span>
              </h1>
              <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto glass rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none">
                <h2 className="text-3xl font-bold text-white mb-6">1. Acceptance of Terms</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  By accessing and using JackpotQuantum.com ("the Website"), you accept and agree to be bound by the terms and 
                  provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">2. Age Restrictions</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  You must be at least 18 years old to use this website. By using this website, you represent and warrant that you 
                  are at least 18 years of age. We reserve the right to request proof of age at any time. If you are under 18, you 
                  must not use this website.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">3. Use License</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Permission is granted to temporarily access the materials on JackpotQuantum.com for personal, non-commercial 
                  transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose or for any public display</li>
                  <li>Attempt to reverse engineer any software contained on the website</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                  <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">4. Disclaimer</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The materials on JackpotQuantum.com are provided on an 'as is' basis. We make no warranties, expressed or implied, 
                  and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions 
                  of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation 
                  of rights.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">5. Limitations</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  In no event shall JackpotQuantum.com or its suppliers be liable for any damages (including, without limitation, 
                  damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the 
                  materials on JackpotQuantum.com, even if we or an authorized representative has been notified orally or in writing of 
                  the possibility of such damage.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">6. Accuracy of Materials</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The materials appearing on JackpotQuantum.com could include technical, typographical, or photographic errors. We do 
                  not warrant that any of the materials on its website are accurate, complete, or current. We may make changes to the 
                  materials contained on its website at any time without notice. However, we do not make any commitment to update the 
                  materials.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">7. Affiliate Relationships</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  JackpotQuantum.com may receive compensation from casinos and gambling operators when users click on links or sign up 
                  through our website. This affiliate relationship does not influence our reviews, ratings, or recommendations. We are 
                  committed to providing honest, unbiased information regardless of affiliate relationships.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">8. Responsible Gambling</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We promote responsible gambling and encourage all users to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Gamble only with money you can afford to lose</li>
                  <li>Set time and money limits before gambling</li>
                  <li>Never chase losses</li>
                  <li>Take regular breaks from gambling</li>
                  <li>Seek help if gambling becomes a problem</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mb-6">
                  If you feel you may have a gambling problem, please seek help from organizations such as BeGambleAware, GamCare, or 
                  Gambling Therapy.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">9. Links to Third-Party Websites</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Our website may contain links to third-party websites that are not owned or controlled by JackpotQuantum.com. We 
                  have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party 
                  websites. You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by or in 
                  connection with the use of any such content, goods, or services available on or through any such websites.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">10. User Content</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  If you submit any content to our website (including comments, reviews, or other materials), you grant us a worldwide, 
                  non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such content 
                  in any media. You represent and warrant that you own or have the necessary rights to grant this license.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">11. Prohibited Uses</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  You may not use our website:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>In any way that violates any applicable law or regulation</li>
                  <li>To transmit any malicious code or viruses</li>
                  <li>To impersonate or attempt to impersonate us or any other person or entity</li>
                  <li>In any way that infringes upon the rights of others</li>
                  <li>To engage in any other conduct that restricts or inhibits anyone's use of the website</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">12. Modifications</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We reserve the right to revise these terms of service at any time without notice. By using this website, you are 
                  agreeing to be bound by the then current version of these terms of service. We may also modify or discontinue any 
                  aspect of the website at any time.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">13. Governing Law</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  These terms and conditions are governed by and construed in accordance with the laws of the United Kingdom. Any 
                  disputes relating to these terms and conditions will be subject to the exclusive jurisdiction of the courts of England 
                  and Wales.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">14. Contact Information</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-dark-700 rounded-lg p-6 mb-6">
                  <p className="text-gray-300">
                    <strong className="text-white">Email:</strong> legal@jackpotquantum.com<br />
                    <strong className="text-white">Website:</strong> jackpotquantum.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Terms;

